import { ResolvedCustomerType } from 'shared-types'
import { BaseMoney } from 'shared-types/src/commercetools'

export enum ProductPriceDiscountType {
  Promo = 'promo',
  Member = 'member',
  Trade = 'trade',
  Staff = 'staff',
}

export interface ProductPriceDisplayInput {
  /**
   * The price the customer pays
   */
  finalPrice?: BaseMoney | null
  /**
   * The RR / everyday price
   */
  regularPrice?: BaseMoney | null
  /**
   * The member customer group price, offered to guests and Standard customers as an incentive
   */
  memberPrice?: BaseMoney | null
  /**
   * The final price is reduced by a promo
   */
  hasPromoDiscount?: boolean
  /**
   * The final price comes from customer type pricing
   */
  hasCustomerTypeDiscount?: boolean
  /**
   * The customer type the final price belongs to. Empty for guests.
   */
  resolvedCustomerType?: ResolvedCustomerType
}

export interface ProductPriceDisplay {
  finalPrice?: BaseMoney | null
  regularPrice?: BaseMoney | null
  hasPromoDiscount: boolean
  hasCustomerTypeDiscount: boolean
  resolvedCustomerType?: ResolvedCustomerType
  /**
   * A guest is looking at a product that has a member price
   */
  isGuestWithMemberPrice: boolean
  /**
   * A signed-in customer outside the member program is looking at a product that has a member price
   */
  isStandardWithMemberPrice: boolean
}

const CUSTOMER_TYPE_DISCOUNT_TYPES: Partial<
  Record<ResolvedCustomerType, ProductPriceDiscountType>
> = {
  [ResolvedCustomerType.Member]: ProductPriceDiscountType.Member,
  [ResolvedCustomerType.Trade]: ProductPriceDiscountType.Trade,
  [ResolvedCustomerType.Staff]: ProductPriceDiscountType.Staff,
}

/**
 * The badge label for a customer type discount. Member pricing is marketed as "Club Price".
 */
const CUSTOMER_TYPE_BADGE_LABELS: Partial<
  Record<ResolvedCustomerType, string>
> = {
  [ResolvedCustomerType.Member]: 'Club Price',
  [ResolvedCustomerType.Trade]: 'Trade price',
  [ResolvedCustomerType.Staff]: 'Staff price',
}

/**
 * Decides which price a product shows and how it is flagged.
 *
 * Guests have no resolved customer type. When a member price exists they are shown the member
 * price as an incentive to sign in, and Standard customers see the same incentive with a prompt
 * to upgrade their account. A promo keeps the On Sale view, and Member, Trade and Staff customers
 * keep the price they are entitled to.
 */
export const resolveProductPriceDisplay = ({
  finalPrice,
  regularPrice,
  memberPrice,
  hasPromoDiscount = false,
  hasCustomerTypeDiscount = false,
  resolvedCustomerType,
}: ProductPriceDisplayInput): ProductPriceDisplay => {
  const hasMemberPrice = !!memberPrice?.centAmount
  const isGuestWithMemberPrice = !resolvedCustomerType && hasMemberPrice
  const isStandardWithMemberPrice =
    resolvedCustomerType === ResolvedCustomerType.Standard && hasMemberPrice
  const isShowMemberPriceIncentive =
    (isGuestWithMemberPrice || isStandardWithMemberPrice) && !hasPromoDiscount

  if (isShowMemberPriceIncentive) {
    return {
      finalPrice: memberPrice,
      regularPrice,
      hasPromoDiscount: false,
      hasCustomerTypeDiscount: true,
      resolvedCustomerType: ResolvedCustomerType.Member,
      isGuestWithMemberPrice,
      isStandardWithMemberPrice,
    }
  }

  return {
    finalPrice,
    regularPrice,
    hasPromoDiscount,
    hasCustomerTypeDiscount,
    resolvedCustomerType,
    isGuestWithMemberPrice,
    isStandardWithMemberPrice,
  }
}

/**
 * The badge shown next to a discounted price. A promo always wins over customer type pricing.
 * Standard customers have no customer type badge, so without a promo they see none.
 */
export const getProductPriceBadge = ({
  hasPromoDiscount = false,
  hasCustomerTypeDiscount = false,
  resolvedCustomerType,
}: Pick<
  ProductPriceDisplayInput,
  'hasPromoDiscount' | 'hasCustomerTypeDiscount' | 'resolvedCustomerType'
>): { label: string; type?: ProductPriceDiscountType } => {
  if (hasPromoDiscount) {
    return { label: 'On special', type: ProductPriceDiscountType.Promo }
  }

  const isStandardCustomer =
    resolvedCustomerType === ResolvedCustomerType.Standard

  if (hasCustomerTypeDiscount && resolvedCustomerType && !isStandardCustomer) {
    return {
      label:
        CUSTOMER_TYPE_BADGE_LABELS[resolvedCustomerType] ??
        `${resolvedCustomerType} price`,
      type: CUSTOMER_TYPE_DISCOUNT_TYPES[resolvedCustomerType],
    }
  }

  return { label: '' }
}

/**
 * The everyday price row is shown when the customer pays less than the RR price. Staff only see
 * it for promos, since their price is always below RR.
 */
export const isShowEverydayPrice = ({
  finalPrice,
  regularPrice,
  hasPromoDiscount = false,
  resolvedCustomerType,
}: Pick<
  ProductPriceDisplayInput,
  'finalPrice' | 'regularPrice' | 'hasPromoDiscount' | 'resolvedCustomerType'
>) => {
  const isBelowRegularPrice =
    !!regularPrice?.centAmount &&
    !!finalPrice?.centAmount &&
    regularPrice.centAmount > finalPrice.centAmount
  const isStaff = resolvedCustomerType === ResolvedCustomerType.Staff

  return isBelowRegularPrice && (hasPromoDiscount || !isStaff)
}
